let API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
let BFF_BASE_URL = (process.env.REACT_APP_BASE_API_URL || '').replace(/\/+$/, '');

let REFRESH_URL = `${BFF_BASE_URL}/refresh`;
let LOGOUT_URL = `${BFF_BASE_URL}/logout`;
let onUnauthorized = null;

export function configureApi({ baseUrl, bffBaseUrl, refreshUrl, logoutUrl, onUnauthorizedCb } = {}) {
    if (baseUrl) {
        API_BASE_URL = baseUrl.replace(/\/+$/, '');
    }

    if (bffBaseUrl) {
        BFF_BASE_URL = bffBaseUrl.replace(/\/+$/, '');
        REFRESH_URL = `${BFF_BASE_URL}/refresh`;
        LOGOUT_URL = `${BFF_BASE_URL}/logout`;
    }

    if (refreshUrl) REFRESH_URL = refreshUrl;
    if (logoutUrl) LOGOUT_URL = logoutUrl;
    if (typeof onUnauthorizedCb === 'function') onUnauthorized = onUnauthorizedCb;
}

function normalizeUrl(url) {
    if (!url) return API_BASE_URL;
    if (/^https?:\/\//i.test(url)) return url;
    if (!API_BASE_URL) return url;
    return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

function getCookie(name) {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[$()*+./?[\\\]^{|}-]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
}

function csrfUrlFor() {
    return bffUrl('/csrf');
}

function fetchWithTimeout(url, options = {}, timeout = 10000, normalizer = normalizeApiUrl) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    return fetch(normalizer(url), {
        ...options,
        signal: controller.signal
    }).finally(() => clearTimeout(id));
}

async function ensureCsrf() {
    if (!getCookie('XSRF-TOKEN')) {
        try {
            await fetchWithTimeout(csrfUrlFor(), { credentials: 'include' }, 2000);
        } catch (_) {
            // Der eigentliche Request liefert danach ggf. den verwertbaren Fehler.
        }
    }
    return getCookie('XSRF-TOKEN') || '';
}

let refreshPromise = null;

function isAuthExpired(status, json) {
    if (status === 401 || status === 419) return true;
    const msg = (json?.error || json?.message || '').toString().toUpperCase();
    return msg.includes('TOKEN_EXPIRED') || msg.includes('JWT_EXPIRED') || msg.includes('SESSION_EXPIRED');
}

async function parseJsonResponse(res) {
    const text = await res.text();
    try {
        return text ? JSON.parse(text) : {};
    } catch {
        return { raw: text };
    }
}

async function refreshSessionOnce() {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
        const res = await fetchWithTimeout(REFRESH_URL, {
            method: 'GET',
            credentials: 'include',
            headers: { Accept: 'application/json' }
        }, 10000, normalizeBffUrl);
        const json = await parseJsonResponse(res);
        if (!res.ok || json?.ok === false) {
            const err = new Error(json?.message || json?.error || res.statusText || 'Refresh failed');
            err.status = res.status;
            err.payload = json;
            throw err;
        }
        await new Promise(r => setTimeout(r, 50));
        return json;
    })();
    try {
        return await refreshPromise;
    } finally {
        refreshPromise = null;
    }
}

function buildBodyAndHeaders(data, headers) {
    if (data == null) return null;

    const isForm = (typeof FormData !== 'undefined') && (data instanceof FormData);
    const isBlob = (typeof Blob !== 'undefined') && (data instanceof Blob);
    const isFile = (typeof File !== 'undefined') && (data instanceof File);
    const isArrayBuffer = (typeof ArrayBuffer !== 'undefined') && (data instanceof ArrayBuffer);

    if (isForm || isBlob || isFile) return data;
    if (isArrayBuffer) {
        if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/octet-stream');
        return data;
    }

    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    return JSON.stringify(data);
}

async function handleUnauthorized(res, json) {
    try {
        void fetchWithTimeout(LOGOUT_URL, {
            method: 'GET',
            credentials: 'include',
            headers: { Accept: 'application/json' }
        }, 1500, normalizeBffUrl);
    } catch (_) {
        // ignore
    }
    if (typeof onUnauthorized === 'function') {
        try { onUnauthorized(); } catch (_) {}
    }
    const err = new Error('SESSION_EXPIRED');
    err.status = res.status;
    err.payload = json;
    throw err;
}

export async function apiCall(url, method = 'GET', data = null, opts = {}) {
    const headers = new Headers(opts.headers || undefined);
    const upper = method.toUpperCase();
    const isMutating = !['GET', 'HEAD', 'OPTIONS'].includes(upper);
    const body = buildBodyAndHeaders(data, headers);

    if (isMutating) {
        const csrf = await ensureCsrf();
        if (csrf) headers.set('x-csrf', csrf);
    }

    const isForm = (typeof FormData !== 'undefined') && (data instanceof FormData);
    const timeoutMs = opts.timeoutMs ?? (isForm ? 120000 : 30000);

    const run = async () => {
        const res = await fetchWithTimeout(url, {
            method,
            headers,
            body,
            credentials: 'include'
        }, timeoutMs);
        const json = await parseJsonResponse(res);
        return { res, json };
    };

    let { res, json } = await run();
    if (res.ok) return json;

    if (isAuthExpired(res.status, json)) {
        try {
            await refreshSessionOnce();
            if (isMutating) {
                const csrf = await ensureCsrf();
                if (csrf) headers.set('x-csrf', csrf);
            }
            ({ res, json } = await run());
            if (res.ok) return json;
        } catch (_) {
            // unten final behandeln
        }
    }

    const isCsrfFail = isMutating && res.status === 403 && (
        String(json?.error || json?.message || '').toLowerCase().includes('csrf') ||
        String(json).toLowerCase().includes('csrf')
    );
    if (isCsrfFail) {
        const csrf = await ensureCsrf();
        if (csrf) headers.set('x-csrf', csrf);
        ({ res, json } = await run());
        if (res.ok) return json;
    }

    if (isAuthExpired(res.status, json)) {
        await handleUnauthorized(res, json);
    }

    const msg = json?.detail?.message || json?.message || json?.error || json?.msg || res.statusText || 'HTTP error';
    const err = new Error(msg);
    err.status = res.status;
    err.payload = json;
    throw err;
}

export async function bffCall(url, method = 'GET', data = null, opts = {}) {
    const headers = new Headers(opts.headers || undefined);
    const upper = method.toUpperCase();
    const isMutating = !['GET', 'HEAD', 'OPTIONS'].includes(upper);
    const body = buildBodyAndHeaders(data, headers);

    if (isMutating) {
        const csrf = await ensureCsrf();
        if (csrf) headers.set('x-csrf', csrf);
    }

    const timeoutMs = opts.timeoutMs ?? 30000;

    const res = await fetchWithTimeout(url, {
        method,
        headers,
        body,
        credentials: 'include'
    }, timeoutMs, normalizeBffUrl);

    const json = await parseJsonResponse(res);

    if (res.ok) return json;

    const msg =
        json?.detail?.message ||
        json?.message ||
        json?.error ||
        json?.msg ||
        res.statusText ||
        'HTTP error';

    const err = new Error(msg);
    err.status = res.status;
    err.payload = json;
    throw err;
}

export async function downloadFile(url, { method = 'GET', body = null, data = null, timeoutMs = 60000, headers: customHeaders = {} } = {}) {
    const headers = new Headers(customHeaders || undefined);
    const upper = method.toUpperCase();
    const isMutating = !['GET', 'HEAD', 'OPTIONS'].includes(upper);
    const requestBody = data != null ? buildBodyAndHeaders(data, headers) : body;

    if (isMutating) {
        const csrf = await ensureCsrf();
        if (csrf) headers.set('x-csrf', csrf);
    }

    const run = async () => fetchWithTimeout(url, {
        method,
        body: requestBody,
        headers,
        credentials: 'include'
    }, timeoutMs);

    let res = await run();
    if (res.ok) return res;

    let json = {};
    try {
        json = await parseJsonResponse(res);
    } catch (_) {
        json = {};
    }

    if (isAuthExpired(res.status, json)) {
        try {
            await refreshSessionOnce();
            if (isMutating) {
                const csrf = await ensureCsrf();
                if (csrf) headers.set('x-csrf', csrf);
            }
            res = await run();
            if (res.ok) return res;
            try { json = await parseJsonResponse(res); } catch (_) {}
        } catch (_) {
            // unten final behandeln
        }
    }

    if (isAuthExpired(res.status, json)) {
        await handleUnauthorized(res, json);
    }

    const msg = json?.detail?.message || json?.message || json?.error || res.statusText || 'HTTP error';
    const err = new Error(msg);
    err.status = res.status;
    err.payload = json;
    throw err;
}

export function apiUrl(path = '') {
    if (/^https?:\/\//i.test(path)) return path;
    return `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

export function bffUrl(path = '') {
    if (/^https?:\/\//i.test(path)) return path;
    return `${BFF_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

function normalizeApiUrl(url) {
    if (!url) return API_BASE_URL;
    if (/^https?:\/\//i.test(url)) return url;
    return apiUrl(url);
}

function normalizeBffUrl(url) {
    if (!url) return BFF_BASE_URL;
    if (/^https?:\/\//i.test(url)) return url;
    return bffUrl(url);
}